import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Fingerprint, Trash, Plus, ClockCounterClockwise, DeviceMobile,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  listCredentials,
  deleteCredential,
  usePasskey,
} from "@/lib/webauthn";

/**
 * PasskeySettings — "Registered devices" pane.
 *
 * Lets the user see every passkey enrolled on their account, add a new
 * one from the current device, or revoke a lost/old one.
 */
export default function PasskeySettings() {
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");

  const {
    supported, platformAvailable, pending: enrolling, registerDevice,
  } = usePasskey();

  const refresh = async () => {
    setLoading(true);
    try {
      setCreds(await listCredentials());
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onEnrol = async () => {
    try {
      const chosen = label.trim() || defaultDeviceLabel();
      await registerDevice(chosen);
      toast.success(`Passkey saved for "${chosen}"`);
      setLabel("");
      refresh();
    } catch (e) {
      if (!/cancel/i.test(String(e?.message || ""))) {
        toast.error(e?.message || "Passkey registration failed");
      }
    }
  };

  const onRevoke = async (id, name) => {
    if (!window.confirm(`Revoke "${name}"? You won't be able to sign in with this device's biometrics anymore.`)) return;
    try {
      await deleteCredential(id);
      toast.success("Passkey revoked");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke passkey");
    }
  };

  return (
    <section
      className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.18)] p-5 sm:p-8"
      data-testid="passkey-settings"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center shrink-0">
          <Fingerprint size={20} weight="regular" className="text-slate-700" />
        </div>
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
            <span className="w-6 h-px bg-brand-blue" />
            Registered devices
          </div>
          <h2 className="mt-2 font-heading font-black text-slate-900 text-xl sm:text-2xl tracking-[-0.02em]">
            Passkeys & biometric sign-in
          </h2>
          <p className="mt-2 text-slate-600 text-[13.5px] leading-[1.7] max-w-xl">
            Sign in to SRES with Face ID, Touch ID, Windows Hello or your device
            PIN — no password needed. Passkeys are stored securely on this device
            and never shared with our servers.
          </p>
        </div>
      </div>

      {/* ── Existing passkeys ─────────────────────────────────────── */}
      <div className="mt-7">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-slate-500 mb-3">
          Your passkeys
        </div>

        {loading ? (
          <div className="text-[13px] text-slate-500 py-4">Loading…</div>
        ) : creds.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-[13px] text-slate-500" data-testid="passkey-empty">
            No passkeys yet — enrol this device below to enable biometric sign-in.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 overflow-hidden" data-testid="passkey-list">
            {creds.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-4 py-3.5 bg-white" data-testid={`passkey-${c.id}`}>
                <div className="w-9 h-9 rounded-md border border-slate-200 flex items-center justify-center shrink-0">
                  <DeviceMobile size={16} weight="regular" className="text-slate-700" />
                </div>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">{c.device_label || "Passkey"}</div>
                  <div className="mt-0.5 flex items-center gap-2.5 text-[11.5px] text-slate-500">
                    <span>Added {formatDate(c.created_at)}</span>
                    {c.last_used_at ? (
                      <span className="inline-flex items-center gap-1">
                        <ClockCounterClockwise size={11} weight="regular" />
                        Last used {formatDate(c.last_used_at)}
                      </span>
                    ) : (
                      <span className="text-slate-400">Never used yet</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRevoke(c.id, c.device_label || "this passkey")}
                  className="text-slate-400 hover:text-red-600 transition-colors shrink-0 p-2 -mr-2 rounded-md hover:bg-red-50"
                  aria-label={`Revoke passkey ${c.device_label}`}
                  data-testid={`passkey-revoke-${c.id}`}
                >
                  <Trash size={16} weight="regular" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Add new ───────────────────────────────────────────────── */}
      {supported ? (
        <div className="mt-8 pt-6 border-t border-slate-100">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-slate-500 mb-3">
            Enrol this device
          </div>
          {platformAvailable ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={defaultDeviceLabel()}
                className="flex-1 h-12 rounded-md bg-slate-50 border border-slate-200 px-4 text-[15px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:bg-white"
                data-testid="passkey-label-input"
              />
              <Button
                onClick={onEnrol}
                disabled={enrolling}
                className="h-12 bg-slate-900 hover:bg-black text-white font-semibold rounded-md text-[14px] inline-flex items-center justify-center gap-2 sm:min-w-[210px]"
                data-testid="passkey-enrol-btn"
              >
                <Plus size={16} weight="bold" />
                {enrolling ? "Waiting for biometrics…" : "Add passkey"}
              </Button>
            </div>
          ) : (
            <p className="text-[13px] text-slate-500 leading-relaxed">
              No biometric authenticator detected on this device (Face ID, Touch ID,
              fingerprint reader, or Windows Hello). You can still sign in with your
              password.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-8 pt-6 border-t border-slate-100 text-[13px] text-slate-500">
          Passkeys aren&apos;t supported in this browser. Use a recent version of
          Chrome, Safari, Firefox or Edge.
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function defaultDeviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? "Android phone" : "Android tablet";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux";
  return "This device";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
